import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { parseXmlToRuleSet, ruleSetToXml } from '../utils/xmlParser';
import { validateRuleSet } from '../utils/validator';
import config from '../config';

const RuleContext = createContext();

export function useRules() {
  return useContext(RuleContext);
}

export function RuleProvider({ children, user }) {
  const [locationGroups, setLocationGroups] = useState([]);
  const [carrierGroups, setCarrierGroups] = useState([]);
  const [feeRules, setFeeRules] = useState([]);
  const [testRuleSet, setTestRuleSet] = useState({ carrierGroups: [], feeRules: [], locationGroups: [] });
  const [changeLog, setChangeLog] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Log action locally AND to backend
  const addChangeLog = useCallback((action, details) => {
    const username = user?.username || 'system';
    const entry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      action,
      details: `[${username}] ${details}`,
      username,
    };
    setChangeLog((prev) => [entry, ...prev].slice(0, 100));

    // Log to backend
    fetch(`${config.API_BASE}/audit/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, action, details }),
    }).catch(() => {});
  }, [user]);

  // Load from DB on mount
  const fetchFromDb = useCallback(async () => {
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`);
      const data = await res.json();
      if (data.success && data.content) {
        const parsed = parseXmlToRuleSet(data.content);
        setLocationGroups(parsed.locationGroups || []);
        setCarrierGroups(parsed.carrierGroups || []);
        setFeeRules(parsed.feeRules || []);
        if (parsed.testRuleSet) setTestRuleSet(parsed.testRuleSet);
        setLoaded(true);
        return { success: true };
      }
    } catch { /* backend offline */ }
    setLoaded(true);
    return { success: false };
  }, []);

  useEffect(() => { fetchFromDb(); }, [fetchFromDb]);

  // Save current state to DB (admin only)
  const saveToDb = useCallback(async () => {
    if (user?.role !== 'ADMIN') {
      toast.error('Only admin can save to the master file');
      return false;
    }
    const xml = ruleSetToXml({ locationGroups, carrierGroups, feeRules, testRuleSet });
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: xml, username: user.username, role: user.role }),
      });
      const data = await res.json();
      if (data.success) {
        addChangeLog('SAVE_TO_DB', `Saved to database v${data.version}`);
        toast.success(`Saved to database (v${data.version})`);
        return true;
      } else {
        toast.error(data.message || 'Save failed');
      }
    } catch { toast.error('Backend not available'); }
    return false;
  }, [locationGroups, carrierGroups, feeRules, testRuleSet, user, addChangeLog]);

  // Submit entire modified file for approval (agent)
  const submitForApproval = useCallback(async (description) => {
    const xml = ruleSetToXml({ locationGroups, carrierGroups, feeRules, testRuleSet });
    try {
      const res = await fetch(`${config.API_BASE}/approvals/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user?.username,
          action: 'SAVE_FILE',
          description: description || 'Agent file changes',
          ruleData: { content: xml, ruleCount: feeRules.length },
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Submitted for admin approval!');
        addChangeLog('SUBMIT_APPROVAL', description || 'Submitted changes for approval');
        return true;
      } else {
        toast.error(data.message || 'Submit failed');
      }
    } catch { toast.error('Backend not available'); }
    return false;
  }, [user, locationGroups, carrierGroups, feeRules, testRuleSet, addChangeLog]);

  // === Fee Rules CRUD ===
  const addFeeRule = useCallback((rule) => {
    setFeeRules((prev) => {
      const existing = prev.find(r => r.sequenceNum === rule.sequenceNum);
      if (existing) { toast.error(`Seq #${rule.sequenceNum} exists!`); return prev; }
      const updated = [...prev, rule].sort((a, b) => a.sequenceNum - b.sequenceNum);
      addChangeLog('ADD_RULE', `Added rule #${rule.sequenceNum} carrier "${rule.carrier}"`);
      toast.success(`Rule #${rule.sequenceNum} added`);
      return updated;
    });
  }, [addChangeLog]);

  const insertFeeRule = useCallback((rule) => {
    setFeeRules((prev) => {
      const shifted = prev.map(r => r.sequenceNum >= rule.sequenceNum ? { ...r, sequenceNum: r.sequenceNum + 1 } : r);
      const updated = [...shifted, rule].sort((a, b) => a.sequenceNum - b.sequenceNum);
      addChangeLog('INSERT_RULE', `Inserted rule #${rule.sequenceNum} carrier "${rule.carrier}"`);
      toast.success(`Rule #${rule.sequenceNum} inserted`);
      return updated;
    });
  }, [addChangeLog]);

  const updateFeeRule = useCallback((oldSeqNum, rule) => {
    setFeeRules((prev) => {
      const updated = prev.map(r => r.sequenceNum === oldSeqNum ? rule : r).sort((a, b) => a.sequenceNum - b.sequenceNum);
      addChangeLog('UPDATE_RULE', `Updated rule #${rule.sequenceNum} carrier "${rule.carrier}"`);
      toast.success(`Rule #${rule.sequenceNum} updated`);
      return updated;
    });
  }, [addChangeLog]);

  const deleteFeeRule = useCallback((sequenceNum) => {
    setFeeRules((prev) => {
      addChangeLog('DELETE_RULE', `Deleted rule #${sequenceNum}`);
      toast.success(`Rule #${sequenceNum} deleted`);
      return prev.filter(r => r.sequenceNum !== sequenceNum);
    });
  }, [addChangeLog]);

  const deleteFeeRuleAndResequence = useCallback((sequenceNum) => {
    setFeeRules((prev) => {
      const filtered = prev.filter(r => r.sequenceNum !== sequenceNum);
      const updated = filtered.map(r => r.sequenceNum > sequenceNum ? { ...r, sequenceNum: r.sequenceNum - 1 } : r);
      addChangeLog('DELETE_RESEQUENCE', `Deleted rule #${sequenceNum} and resequenced`);
      toast.success(`Rule #${sequenceNum} deleted & resequenced`);
      return updated;
    });
  }, [addChangeLog]);

  const bulkDeleteAndResequence = useCallback((sequenceNums) => {
    setFeeRules((prev) => {
      const filtered = prev.filter(r => !sequenceNums.includes(r.sequenceNum));
      const updated = filtered.sort((a, b) => a.sequenceNum - b.sequenceNum).map((r, idx) => ({ ...r, sequenceNum: idx + 1 }));
      addChangeLog('BULK_DELETE', `Deleted ${sequenceNums.length} rules & renumbered`);
      toast.success(`Deleted ${sequenceNums.length} rules`);
      return updated;
    });
  }, [addChangeLog]);

  const bulkExtendDates = useCallback((selectedSeqNums, travelDate, ticketingDate) => {
    setFeeRules((prev) => {
      const updated = prev.map(r => {
        if (!selectedSeqNums.includes(r.sequenceNum)) return r;
        const m = { ...r };
        // Only extend dates that already exist on the rule
        if (travelDate && r.travelDateDiscontinue) m.travelDateDiscontinue = travelDate;
        if (ticketingDate && r.ticketingDateDiscontinue) m.ticketingDateDiscontinue = ticketingDate;
        return m;
      });
      addChangeLog('BULK_EXTEND', `Extended dates for ${selectedSeqNums.length} rules`);
      toast.success(`Extended dates for ${selectedSeqNums.length} rules`);
      return updated;
    });
  }, [addChangeLog]);

  const bulkUpdateCarrier = useCallback((selectedSeqNums, newCarrier) => {
    setFeeRules((prev) => {
      const updated = prev.map(r => selectedSeqNums.includes(r.sequenceNum) ? { ...r, carrier: newCarrier } : r);
      addChangeLog('BULK_CARRIER', `Updated carrier to "${newCarrier}" for ${selectedSeqNums.length} rules`);
      toast.success(`Updated carrier for ${selectedSeqNums.length} rules`);
      return updated;
    });
  }, [addChangeLog]);

  // === Location Groups ===
  const addLocationGroup = useCallback((group) => {
    setLocationGroups((prev) => {
      if (prev.find(g => g.tableNum === group.tableNum)) { toast.error(`Group #${group.tableNum} exists!`); return prev; }
      addChangeLog('ADD_LOCATION_GROUP', `Added location group #${group.tableNum} (${group.locations.length} locations)`);
      toast.success(`Location group #${group.tableNum} added`);
      return [...prev, group].sort((a, b) => parseInt(a.tableNum) - parseInt(b.tableNum));
    });
  }, [addChangeLog]);

  const updateLocationGroup = useCallback((tableNum, newLocations) => {
    setLocationGroups((prev) => {
      addChangeLog('UPDATE_LOCATION_GROUP', `Updated location group #${tableNum} (${newLocations.length} locations)`);
      toast.success(`Location group #${tableNum} updated`);
      return prev.map(g => g.tableNum === tableNum ? { ...g, locations: newLocations } : g);
    });
  }, [addChangeLog]);

  const deleteLocationGroup = useCallback((tableNum) => {
    setLocationGroups((prev) => {
      addChangeLog('DELETE_LOCATION_GROUP', `Deleted location group #${tableNum}`);
      toast.success(`Location group #${tableNum} deleted`);
      return prev.filter(g => g.tableNum !== tableNum);
    });
  }, [addChangeLog]);

  // === Carrier Groups ===
  const addCarrierGroup = useCallback((group) => {
    setCarrierGroups((prev) => {
      if (prev.find(g => g.tableNum === group.tableNum)) { toast.error(`Group #${group.tableNum} exists!`); return prev; }
      addChangeLog('ADD_CARRIER_GROUP', `Added carrier group #${group.tableNum} (${group.carriers.length} carriers)`);
      toast.success(`Carrier group #${group.tableNum} added`);
      return [...prev, group].sort((a, b) => parseInt(a.tableNum) - parseInt(b.tableNum));
    });
  }, [addChangeLog]);

  const updateCarrierGroup = useCallback((tableNum, newCarriers) => {
    setCarrierGroups((prev) => {
      addChangeLog('UPDATE_CARRIER_GROUP', `Updated carrier group #${tableNum} (${newCarriers.length} carriers)`);
      toast.success(`Carrier group #${tableNum} updated`);
      return prev.map(g => g.tableNum === tableNum ? { ...g, carriers: newCarriers } : g);
    });
  }, [addChangeLog]);

  const deleteCarrierGroup = useCallback((tableNum) => {
    setCarrierGroups((prev) => {
      addChangeLog('DELETE_CARRIER_GROUP', `Deleted carrier group #${tableNum}`);
      toast.success(`Carrier group #${tableNum} deleted`);
      return prev.filter(g => g.tableNum !== tableNum);
    });
  }, [addChangeLog]);

  // === XML Load/Export ===
  const loadFromXml = useCallback((xmlString) => {
    try {
      const parsed = parseXmlToRuleSet(xmlString);
      const errors = validateRuleSet(parsed);
      if (errors.length > 0) { errors.forEach(e => toast.error(e)); return { success: false, errors }; }
      setLocationGroups(parsed.locationGroups || []);
      setCarrierGroups(parsed.carrierGroups || []);
      setFeeRules(parsed.feeRules || []);
      if (parsed.testRuleSet) setTestRuleSet(parsed.testRuleSet);
      addChangeLog('UPLOAD', `Loaded XML: ${parsed.feeRules?.length || 0} rules`);
      toast.success('XML loaded!');
      return { success: true, errors: [] };
    } catch (err) {
      toast.error(`Parse error: ${err.message}`);
      return { success: false, errors: [err.message] };
    }
  }, [addChangeLog]);

  const exportToXml = useCallback(() => {
    return ruleSetToXml({ locationGroups, carrierGroups, feeRules, testRuleSet });
  }, [locationGroups, carrierGroups, feeRules, testRuleSet]);

  // === Conflict/Expired Detection ===
  const getConflictingRules = useCallback(() => {
    const conflicts = [];
    for (let i = 0; i < feeRules.length; i++) {
      for (let j = i + 1; j < feeRules.length; j++) {
        const a = feeRules[i], b = feeRules[j];
        if (a.carrier !== b.carrier) continue;
        if ((a.origin || '') !== (b.origin || '')) continue;
        if ((a.destination || '') !== (b.destination || '')) continue;
        if ((a.cabinClass || '') !== (b.cabinClass || '')) continue;
        if ((a.tripType || '') !== (b.tripType || '')) continue;
        if ((a.priceSource || '') !== (b.priceSource || '')) continue;
        if ((a.interline || '') !== (b.interline || '')) continue;
        if ((a.sliceInterline || '') !== (b.sliceInterline || '')) continue;
        if ((a.passengerType || '') !== (b.passengerType || '')) continue;
        if ((a.minSegments || '') !== (b.minSegments || '')) continue;
        // Price range check
        if (a.minPrice || a.maxPrice || b.minPrice || b.maxPrice) {
          const aMin = a.minPrice ? parseFloat(a.minPrice.replace(/[A-Z]/g, '')) : 0;
          const aMax = a.maxPrice ? parseFloat(a.maxPrice.replace(/[A-Z]/g, '')) : Infinity;
          const bMin = b.minPrice ? parseFloat(b.minPrice.replace(/[A-Z]/g, '')) : 0;
          const bMax = b.maxPrice ? parseFloat(b.maxPrice.replace(/[A-Z]/g, '')) : Infinity;
          if (aMax <= bMin || bMax <= aMin) continue;
        }
        // Date range check
        if (a.travelDateEffective || a.travelDateDiscontinue || b.travelDateEffective || b.travelDateDiscontinue) {
          const aS = a.travelDateEffective ? new Date(a.travelDateEffective) : new Date('1970-01-01');
          const aE = a.travelDateDiscontinue ? new Date(a.travelDateDiscontinue) : new Date('2099-12-31');
          const bS = b.travelDateEffective ? new Date(b.travelDateEffective) : new Date('1970-01-01');
          const bE = b.travelDateDiscontinue ? new Date(b.travelDateDiscontinue) : new Date('2099-12-31');
          if (aE <= bS || bE <= aS) continue;
        }
        conflicts.push({ ruleA: a, ruleB: b, reason: `Rule #${b.sequenceNum} shadowed by #${a.sequenceNum}` });
      }
    }
    return conflicts;
  }, [feeRules]);

  const getExpiredRules = useCallback(() => {
    const now = new Date();
    return feeRules.filter(r => (r.travelDateDiscontinue && new Date(r.travelDateDiscontinue) < now) || (r.ticketingDateDiscontinue && new Date(r.ticketingDateDiscontinue) < now));
  }, [feeRules]);

  // Test rule operations
  const addTestFeeRule = useCallback((rule) => {
    setTestRuleSet(prev => {
      if ((prev.feeRules || []).find(r => r.sequenceNum === rule.sequenceNum)) { toast.error(`Test rule #${rule.sequenceNum} exists!`); return prev; }
      addChangeLog('ADD_TEST_RULE', `Added test rule #${rule.sequenceNum}`);
      toast.success(`Test rule #${rule.sequenceNum} added`);
      return { ...prev, feeRules: [...(prev.feeRules || []), rule].sort((a, b) => a.sequenceNum - b.sequenceNum) };
    });
  }, [addChangeLog]);

  const updateTestFeeRule = useCallback((oldSeqNum, rule) => {
    setTestRuleSet(prev => {
      addChangeLog('UPDATE_TEST_RULE', `Updated test rule #${rule.sequenceNum}`);
      toast.success(`Test rule updated`);
      return { ...prev, feeRules: (prev.feeRules || []).map(r => r.sequenceNum === oldSeqNum ? rule : r) };
    });
  }, [addChangeLog]);

  const deleteTestFeeRule = useCallback((seqNum) => {
    setTestRuleSet(prev => {
      addChangeLog('DELETE_TEST_RULE', `Deleted test rule #${seqNum}`);
      toast.success(`Test rule #${seqNum} deleted`);
      return { ...prev, feeRules: (prev.feeRules || []).filter(r => r.sequenceNum !== seqNum) };
    });
  }, [addChangeLog]);

  const addTestCarrierGroup = useCallback((group) => {
    setTestRuleSet(prev => {
      toast.success(`Test carrier group #${group.tableNum} added`);
      return { ...prev, carrierGroups: [...(prev.carrierGroups || []), group] };
    });
  }, []);

  const deleteTestCarrierGroup = useCallback((tableNum) => {
    setTestRuleSet(prev => {
      toast.success(`Test carrier group #${tableNum} deleted`);
      return { ...prev, carrierGroups: (prev.carrierGroups || []).filter(g => g.tableNum !== tableNum) };
    });
  }, []);

  const value = {
    locationGroups, carrierGroups, feeRules, testRuleSet, changeLog, loaded,
    addFeeRule, insertFeeRule, updateFeeRule, deleteFeeRule,
    deleteFeeRuleAndResequence, bulkDeleteAndResequence, bulkExtendDates, bulkUpdateCarrier,
    addLocationGroup, updateLocationGroup, deleteLocationGroup,
    addCarrierGroup, updateCarrierGroup, deleteCarrierGroup,
    loadFromXml, exportToXml, fetchFromDb, saveToDb, submitForApproval,
    getConflictingRules, getExpiredRules,
    setTestRuleSet, setFeeRules,
    addTestFeeRule, updateTestFeeRule, deleteTestFeeRule, addTestCarrierGroup, deleteTestCarrierGroup,
  };

  return <RuleContext.Provider value={value}>{children}</RuleContext.Provider>;
}
